import React from 'react'
import { useTranslation } from 'react-i18next'
import {
    Bar,
    BarChart,
    CartesianGrid,
    Legend,
    ResponsiveContainer,
    Tooltip,
    XAxis,
    YAxis,
} from 'recharts'
import { Typogaphy } from 'vnetwork-ui'
import { Building2, ChevronRight, TrendingUp, Users } from 'lucide-react'
import { CHART_COLORS } from '../workday-dashboard-config'
import type { DrillLevel, DrillState } from '../workday-dashboard-config'

interface DrilldownChartProps {
    chartData: any[]
    drillState: DrillState
    onBarClick: (chartItem: any) => void
    onBreadcrumbClick: (level: DrillLevel) => void
}

/**
 * Drilldown chart component with breadcrumb navigation
 * Displays department or employee workday statistics based on drill level
 */
export const DrilldownChart: React.FC<DrilldownChartProps> = ({
    chartData,
    drillState,
    onBarClick,
    onBreadcrumbClick,
}) => {
    const { t } = useTranslation()

    if (chartData.length === 0) return null

    return (
        <div
            className="col-span-2 bg-white rounded-lg p-4 h-full"
            data-tour="workday-chart"
        >
            {/* Breadcrumb */}
            <div className="flex items-center gap-2 mb-2">
                <TrendingUp className="w-5 h-5 text-primary" />
                <div className="flex items-center gap-1 text-sm">
                    <button
                        onClick={() => onBreadcrumbClick('company')}
                        className={`flex items-center gap-1 ${
                            drillState.level === 'company'
                                ? 'text-primary font-semibold'
                                : 'text-gray-500 hover:text-primary'
                        }`}
                    >
                        <Building2 className="w-4 h-4" />
                        {t('workday.company')}
                    </button>

                    {drillState.level === 'department' && (
                        <>
                            <ChevronRight className="w-4 h-4 text-gray-400" />
                            <span className="flex items-center gap-1 text-primary font-semibold">
                                <Users className="w-4 h-4" />
                                {drillState.departmentName}
                            </span>
                        </>
                    )}
                </div>

                {drillState.level === 'company' && (
                    <Typogaphy className="text-xs text-gray-400 ml-auto">
                        {t('workday.click_to_drill')}
                    </Typogaphy>
                )}
            </div>

            <ResponsiveContainer width="100%" height={300}>
                <BarChart data={chartData}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                    <XAxis
                        dataKey="name"
                        stroke="#9ca3af"
                        fontSize={11}
                        angle={-45}
                        textAnchor="end"
                        height={80}
                        interval={0}
                    />
                    <YAxis stroke="#9ca3af" fontSize={12} />
                    <Tooltip
                        content={({ active, payload }) => {
                            if (!active || !payload?.length) return null
                            const item = payload[0].payload
                            return (
                                <div className="bg-white p-3 border rounded-lg shadow-lg">
                                    <p className="font-semibold text-gray-800 mb-2">
                                        {item.fullName || item.name}
                                    </p>
                                    <div className="space-y-1 text-sm">
                                        {drillState.level === 'company' && (
                                            <p className="text-gray-600">
                                                {t('workday.total_employees')}:{' '}
                                                <span className="font-medium">
                                                    {item.employees}
                                                </span>
                                            </p>
                                        )}
                                        <p className="text-green-600">
                                            {t('workday.attendance_days')}:{' '}
                                            <span className="font-medium">
                                                {item.attendance}
                                            </span>
                                        </p>
                                        <p className="text-red-600">
                                            {t('workday.late_days')}:{' '}
                                            <span className="font-medium">
                                                {item.late}
                                            </span>
                                        </p>
                                        <p className="text-blue-600">
                                            {t('workday.annual_leave')}:{' '}
                                            <span className="font-medium">
                                                {item.leave}
                                            </span>
                                        </p>
                                        <p className="text-purple-600">
                                            {t('workday.remote_days')}:{' '}
                                            <span className="font-medium">
                                                {item.remote}
                                            </span>
                                        </p>
                                    </div>
                                    {drillState.level === 'company' && (
                                        <p className="text-xs text-gray-400 mt-2 border-t pt-2">
                                            {t(
                                                'workday.click_to_view_employees'
                                            )}
                                        </p>
                                    )}
                                </div>
                            )
                        }}
                    />
                    <Legend wrapperStyle={{ fontSize: '11px' }} iconSize={10} />
                    <Bar
                        dataKey="attendance"
                        name={t('workday.attendance_days')}
                        fill={CHART_COLORS.attendance}
                        radius={[4, 4, 0, 0]}
                        cursor={
                            drillState.level === 'company'
                                ? 'pointer'
                                : 'default'
                        }
                        onClick={(data) => onBarClick(data)}
                    />
                    <Bar
                        dataKey="late"
                        name={t('workday.late_days')}
                        fill={CHART_COLORS.late}
                        radius={[4, 4, 0, 0]}
                        cursor={
                            drillState.level === 'company'
                                ? 'pointer'
                                : 'default'
                        }
                        onClick={(data) => onBarClick(data)}
                    />
                    <Bar
                        dataKey="leave"
                        name={t('workday.annual_leave')}
                        fill={CHART_COLORS.leave}
                        radius={[4, 4, 0, 0]}
                        cursor={
                            drillState.level === 'company'
                                ? 'pointer'
                                : 'default'
                        }
                        onClick={(data) => onBarClick(data)}
                    />
                    <Bar
                        dataKey="remote"
                        name={t('workday.remote_days')}
                        fill={CHART_COLORS.remote}
                        radius={[4, 4, 0, 0]}
                        cursor={
                            drillState.level === 'company'
                                ? 'pointer'
                                : 'default'
                        }
                        onClick={(data) => onBarClick(data)}
                    />
                </BarChart>
            </ResponsiveContainer>
        </div>
    )
}
